/**
 * Per-member coverage / completeness dashboard (Phase 4.2) — READ-ONLY.
 *
 * Top: a MUI X DataGrid overview (GET /coverage/summary) of every member with
 * any coverage data — completeness %, gap count, questions scored, last activity.
 * Clicking a row loads that member's detail (GET /coverage/:memberId): the
 * Interview Completeness / Mirror Readiness gauges, a per-dimension coverage-bar
 * breakdown (the Flutter Mirror screen's completeness visual, expressed simply in
 * MUI), and the underlying question×coverage rows.
 *
 * All numbers are computed by quill-engine (mirrorScores / interviewQueueService)
 * and merely displayed here — no scoring logic is duplicated in the frontend.
 *
 * Ported from quill-app/quill-admin's CoverageDashboardPage.tsx. Redux
 * (interviewSlice's coverage* fields) replaced with local useState/useEffect
 * calling quillApi.coverage.* directly. Read-only page — no mutations, so no
 * ActionConfirm is needed here.
 */

import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Stack,
  LinearProgress,
  Paper,
  Button,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Insights as CoverageIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  quillApi,
  CoverageSummaryRow,
  MemberCoverage,
  DimensionCoverage,
} from '../../../services/quillAdminService';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import EmptyState from '../../common/EmptyState';

function pct(n: number | null | undefined): string {
  return n == null ? '—' : `${Math.round(n * 100)}%`;
}

const LABEL_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  rich: 'success',
  developing: 'warning',
  'needs more stories': 'error',
};

const DimensionCoverageBar: React.FC<{ dim: DimensionCoverage }> = ({ dim }) => {
  const value = Math.round(dim.coverage * 100);
  const color = LABEL_COLOR[dim.label] || 'primary';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {dim.display_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {value}% · {dim.label}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        color={color}
        sx={{ height: 10, borderRadius: 5 }}
      />
    </Box>
  );
};

const GaugeCard: React.FC<{ label: string; value: number | null; hint: string }> = ({ label, value, hint }) => (
  <Paper sx={{ p: 2, flex: 1, minWidth: 200 }}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h3" sx={{ fontWeight: 700, my: 0.5 }}>
      {pct(value)}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {hint}
    </Typography>
  </Paper>
);

const MemberDetail: React.FC<{
  memberCoverage: MemberCoverage | null;
  loading: boolean;
  onBack: () => void;
}> = ({ memberCoverage, loading, onBack }) => {
  if (loading && !memberCoverage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!memberCoverage) return null;

  const answered = memberCoverage.questions.filter((q) => q.coverage >= 0.6).length;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Back to all members
      </Button>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Member coverage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
        {memberCoverage.member_id}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <GaugeCard label="Interview completeness" value={memberCoverage.completeness} hint="Σ(priority·coverage) / Σ(priority)" />
        <GaugeCard label="Mirror readiness" value={memberCoverage.readiness} hint="Σ(importance·coverage·confidence) / Σ(importance)" />
        <Paper sx={{ p: 2, flex: 1, minWidth: 200 }}>
          <Typography variant="overline" color="text.secondary">
            Gaps / answered
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, my: 0.5 }}>
            {memberCoverage.gap_count ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {answered}/{memberCoverage.questions.length} questions at ≥60% coverage
          </Typography>
        </Paper>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Coverage by dimension
        </Typography>
        {memberCoverage.dimensions.length === 0 ? (
          <EmptyState
            icon="no_data"
            title="No dimension coverage yet"
            description="This member has no scored interview dimensions. Coverage appears once they answer interview questions."
          />
        ) : (
          memberCoverage.dimensions.map((d) => <DimensionCoverageBar key={d.dimension_key} dim={d} />)
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Questions ({memberCoverage.questions.length})
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <Stack divider={<Divider flexItem />} spacing={1}>
          {memberCoverage.questions.map((q) => (
            <Box key={q.question_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Tooltip title={q.question_text}>
                  <Typography variant="body2" noWrap>
                    {q.question_text}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  {q.module}
                  {q.answered_via ? ` · via ${q.answered_via}` : ''}
                </Typography>
              </Box>
              <Box sx={{ width: 120 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.round(q.coverage * 100)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Typography variant="caption" sx={{ width: 40, textAlign: 'right' }}>
                {pct(q.coverage)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

const CoverageDashboardPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [coverageSummary, setCoverageSummary] = React.useState<CoverageSummaryRow[]>([]);
  const [coverageLoading, setCoverageLoading] = React.useState(false);
  const [memberCoverage, setMemberCoverage] = React.useState<MemberCoverage | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<string | null>(null);

  const loadSummary = React.useCallback(async () => {
    setCoverageLoading(true);
    setError(null);
    try {
      setCoverageSummary(await quillApi.coverage.getSummary());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load coverage summary.');
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const openMember = async (memberId: string) => {
    setSelected(memberId);
    setCoverageLoading(true);
    setError(null);
    try {
      setMemberCoverage(await quillApi.coverage.getMemberCoverage(memberId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load member coverage.');
    } finally {
      setCoverageLoading(false);
    }
  };
  const back = () => {
    setSelected(null);
    setMemberCoverage(null);
  };

  // A fetch error must be recoverable without a full page reload: retry the
  // in-context request — the open member detail if one is selected, else the
  // summary grid.
  const retry = () => {
    if (selected) openMember(selected);
    else loadSummary();
  };

  const columns: GridColDef<CoverageSummaryRow>[] = [
    { field: 'member_id', headerName: 'Member', flex: 1, minWidth: 280 },
    {
      field: 'completeness',
      headerName: 'Completeness',
      width: 180,
      renderCell: (params: GridRenderCellParams<CoverageSummaryRow, number | null>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={params.value == null ? 0 : Math.round(params.value * 100)}
            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ width: 36, textAlign: 'right' }}>
            {pct(params.value)}
          </Typography>
        </Box>
      ),
    },
    { field: 'gap_count', headerName: 'Gaps', width: 90, type: 'number' },
    { field: 'scored_count', headerName: 'Scored', width: 90, type: 'number' },
    {
      field: 'last_activity',
      headerName: 'Last activity',
      width: 190,
      valueFormatter: (value) => (value ? new Date(String(value)).toLocaleString() : '—'),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <CoverageIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Coverage Dashboard
        </Typography>
        <HelpTip id="quill-coverage" />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Per-member completeness, gaps, and dimension coverage across the interview bank.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />
        {!selected && (
          <Button startIcon={<RefreshIcon />} onClick={loadSummary} disabled={coverageLoading}>
            Refresh
          </Button>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {selected ? (
        <MemberDetail memberCoverage={memberCoverage} loading={coverageLoading} onBack={back} />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Members with any interview coverage data, lowest completeness first. Click a row for the
            per-dimension breakdown.
          </Typography>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={coverageSummary}
              columns={columns}
              getRowId={(r) => r.member_id}
              loading={coverageLoading}
              onRowClick={(params) => openMember(String(params.id))}
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50, 100]}
            />
          </Box>
        </>
      )}

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>
    </Box>
  );
};

export default CoverageDashboardPage;
